/**
 * The route gate: one component, mounted once, that decides whether the screen
 * the URL asks for may be rendered.
 *
 * WHY ONE COMPONENT AND NOT 75 WRAPPERS. `MainRoutes` declares about 75 routes.
 * Wrapping each one is 75 chances to forget, and the routes that were forgotten
 * last time (`add-news`, `edit-faculty`, `media-gallery`, `create-speech` and
 * the rest of the add/edit forms) are exactly the ones that matter - a list
 * screen that is merely hidden is a smaller leak than an editor that saves.
 *
 * So this sits inside `GeneralBody`, around the single `<Outlet/>` every
 * `/home` screen renders through. A route cannot be added without passing
 * through it.
 *
 * THE SIDEBAR AND THE HEADER STAY VISIBLE. The refusal replaces the content
 * area only, so an operator who follows a stale link still has their menu and
 * can go somewhere they are allowed. A bare 403 page with no navigation is a
 * dead end.
 */

import React from 'react';
import { useLocation } from 'react-router-dom';

import { usePageAccess } from '../Context/PageAccessContext';
import Loader from '../components/Molecules/Loader/Loader';
import NoPermission from '../components/Template/PageNotFound/NoPermission';

const PageAccessGate = ({ children }) => {
  const location = useLocation();
  const { isLoading, canAccess } = usePageAccess();

  // Hold the content back until the permission lists have resolved. Rendering
  // first and correcting afterwards would flash the screen - and fire its data
  // requests - for a user who may not be allowed to see it at all.
  //
  // The window is short and usually zero: both lists are seeded from
  // `localStorage`, so a returning operator never sees this.
  if (isLoading) return <Loader />;

  if (!canAccess(location.pathname)) return <NoPermission />;

  return children;
};

export default PageAccessGate;
